import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AuthLayout from '../components/layout/AuthLayout'
import { useAppStore } from '@/store'
import { useTranslation, Trans } from '@/i18n'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { Button } from '../components/ui/button'
import { trackEvent } from '@/utils/analytics'
import {
  peekPendingDestination,
  takePendingDestination,
} from '@/utils/pendingDestination'

const REDIRECT_TO = '/intelligent-board'

/**
 * FS-701: the longest this page will show its spinner before giving up on auth
 * initialisation and rendering the sign-in buttons anyway.
 *
 * `keycloakClient` now bounds its own init (15s), so in the reported failure
 * this never fires — the init rejects, the catch below runs, and the buttons
 * appear. This is the backstop for the shape of bug rather than the instance of
 * it: anything under `initializeAuth` that hangs instead of throwing would once
 * again leave the card spinning forever with no way out but a manual refresh.
 * Set above the client's own bound so the specific, better-reported path wins.
 *
 * Rendering the buttons rather than an error is deliberate. Only the *silent*
 * check failed; clicking a provider goes straight to Keycloak and works.
 */
const INIT_SAFETY_TIMEOUT_MS = 20_000

/**
 * FS-663: where Keycloak should return the user.
 *
 * PEEK, not take — Keycloak bounces back to '/' on a cancelled login, and the
 * destination has to survive that. It is consumed at the end of the flow
 * (either the already-logged-in branch below, or OnboardingV2's landing route).
 */
const authRedirectUri = () =>
  window.location.origin + (peekPendingDestination() ?? REDIRECT_TO)

// Shared styling for the footer legal links (Terms of Service / Privacy Policy).
const legalLinkClass =
  'font-medium underline text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300'

const Login = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { userLogged, initializeAuth, login, loginWithGoogle, loginWithMicrosoft, register, keycloakInitialized } = useAppStore()
  const [isInitializing, setIsInitializing] = useState(true)
  const [isFirefox, setIsFirefox] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)

  // Detect Firefox browser
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase()
    const isFirefoxBrowser = userAgent.indexOf('firefox') > -1
    setIsFirefox(isFirefoxBrowser)
  }, [])

  // Initialize Keycloak and check authentication status
  useEffect(() => {
    const init = async () => {
      try {
        // Initialize Keycloak if not already done
        if (!keycloakInitialized) {
          await initializeAuth()
        }
        setIsInitializing(false)
      } catch (error) {
        console.error('Auth initialization error:', error)
        setAuthError('Failed to initialize authentication. Please refresh the page.')
        setIsInitializing(false)
      }
    }

    init()
  }, [initializeAuth, keycloakInitialized])

  // FS-701: bound the spinner. See INIT_SAFETY_TIMEOUT_MS.
  useEffect(() => {
    if (!isInitializing) return
    const timer = setTimeout(() => {
      console.error(
        `Auth initialization did not settle within ${INIT_SAFETY_TIMEOUT_MS}ms; rendering sign-in options.`,
      )
      setIsInitializing(false)
    }, INIT_SAFETY_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [isInitializing])

  // Redirect if already logged in
  useEffect(() => {
    if (!isInitializing && userLogged) {
      // FS-663: an already-authenticated user who clicked a share link lands on
      // '/' — send them to the resource, not the default board.
      navigate(takePendingDestination() ?? REDIRECT_TO)
    }
  }, [isInitializing, userLogged, navigate])

  // Handle Google login - goes directly to Google
  const handleGoogleLogin = () => {
    trackEvent('auth-login_cta_clicked', {
      source: 'login_page',
      cta_text: 'sign_in_with_google',
    })
    loginWithGoogle(authRedirectUri())
  }

  // Handle Microsoft login - goes directly to Microsoft
  const handleMicrosoftLogin = () => {
    trackEvent('auth-login_cta_clicked', {
      source: 'login_page',
      cta_text: 'sign_in_with_microsoft',
    })
    loginWithMicrosoft(authRedirectUri())
  }

  // Handle email login - goes to Keycloak login page
  const handleEmailLogin = () => {
    trackEvent('auth-login_cta_clicked', {
      source: 'login_page',
      cta_text: 'sign_in_with_email',
    })
    login(authRedirectUri())
  }

  // Handle sign up - goes to Keycloak registration page
  const handleSignUp = () => {
    trackEvent('auth-login_cta_clicked', {
      source: 'login_page',
      cta_text: 'sign_up',
    })
    register(authRedirectUri())
  }

  // Show loading while initializing
  if (isInitializing) {
    return (
      <AuthLayout>
        <div className="flex flex-col gap-4 items-center justify-center min-h-[300px]">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 dark:text-gray-500" />
          <p className="text-gray-600 dark:text-gray-400">{t('common:general.loading')}</p>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <div className="flex flex-col gap-4 items-center w-full max-w-[374px]">
        {/* Auth Error Banner */}
        {authError && (
          <div className="w-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3 shadow-sm">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-900 dark:text-red-300 text-sm mb-1">
                {t('common:auth.auth_error_title')}
              </h3>
              <p className="text-red-800 dark:text-red-300 text-xs leading-relaxed">
                {authError}
              </p>
            </div>
          </div>
        )}

        {/* Firefox Warning Banner */}
        {isFirefox && !authError && (
          <div className="w-full bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3 shadow-sm">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 dark:text-amber-300 text-sm mb-1">
                {t('common:auth.firefox_warning_title')}
              </h3>
              <p className="text-amber-800 dark:text-amber-300 text-xs leading-relaxed">
                {t('common:auth.firefox_warning_message')}
              </p>
            </div>
          </div>
        )}

        {/* Heading */}
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-dark-text text-center mb-4">
          {t('common:auth.log_in_title')}
        </h1>

        {/* Continue with Google */}
        <Button
          onClick={handleGoogleLogin}
          variant="outline"
          className="w-full py-3 h-11 text-sm font-medium border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-muted rounded-lg"
        >
          <img src="/images/icon-google.svg" alt="" className="w-4 h-4 mr-1.5" />
          {t('common:auth.continue_with_google')}
        </Button>

        {/* Continue with Microsoft */}
        <Button
          onClick={handleMicrosoftLogin}
          variant="outline"
          className="w-full py-3 h-11 text-sm font-medium border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-muted rounded-lg"
        >
          <img src="/images/icon-microsoft.svg" alt="" className="w-4 h-4 mr-1.5" />
          {t('common:auth.continue_with_microsoft')}
        </Button>

        {/* Continue with Email */}
        <Button
          onClick={handleEmailLogin}
          variant="outline"
          className="w-full py-3 h-11 text-sm font-medium border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-muted rounded-lg"
        >
          {t('common:auth.continue_with_email')}
        </Button>

        {/* Sign up link */}
        <p className="text-sm text-gray-500 dark:text-dark-text-muted text-center mt-4">
          {t('common:auth.no_account')}{' '}
          <button
            onClick={handleSignUp}
            className="text-gray-900 dark:text-dark-text font-medium hover:underline"
          >
            {t('common:auth.sign_up')}
          </button>
        </p>

        {/* Terms agreement */}
        <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-2">
          {/* URLs mirror fabric/toolkit/Workload/Manifest/Product.json — keep in sync if marketing changes a slug. */}
          <Trans
            i18nKey="common:auth.terms_agreement"
            components={{
              terms: (
                <a
                  href="https://www.intuigence.ai/website-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={legalLinkClass}
                />
              ),
              privacy: (
                <a
                  href="https://www.intuigence.ai/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={legalLinkClass}
                />
              ),
            }}
          />
        </p>
      </div>
    </AuthLayout>
  )
}

export default Login
